import { Globe } from "lucide-react";
import { Card } from "@components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@components/ui/table";
import { useState } from "react";

function toCamelCase(str: string) {
  return str
    .toLowerCase()
    .replace(/(^|\s|_)([a-z])/g, (m) => m.toUpperCase())
    .replace(/_/g, " ");
}

export function Countries({
  allPlayerStats,
  loading,
  error,
  startDate,
  endDate,
}: {
  allPlayerStats: any;
  loading: boolean;
  error: string | null;
  startDate?: Date;
  endDate?: Date;
}) {
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Defensive: support both array and object with players array
  let data = Array.isArray(allPlayerStats)
    ? allPlayerStats
    : allPlayerStats &&
      typeof allPlayerStats === "object" &&
      "players" in allPlayerStats &&
      Array.isArray((allPlayerStats as any).players)
    ? (allPlayerStats as any).players
    : [];

  // Filter by date range if provided
  if (startDate || endDate) {
    data = data.filter((p: any) => {
      if (!p.signupDate) return false;
      const d = new Date(p.signupDate);
      if (isNaN(d.getTime())) return false;
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    });
  }

  // Count players by country (prefer countryName, fallback to country, case-insensitive)
  const countryCounts: Record<string, number> = {};
  data.forEach((p: any) => {
    let country = p.countryName || p.country || "Unknown";
    country = country.trim().toUpperCase();
    if (!countryCounts[country]) countryCounts[country] = 0;
    countryCounts[country] += 1;
  });
  // Sort by count descending
  const sorted = Object.entries(countryCounts).sort((a, b) => b[1] - a[1]);
  const totalPages = Math.ceil(sorted.length / pageSize);
  const pagedRows = sorted.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200 overflow-hidden p-6 border border-gray-800">
      <h2 className="text-lg font-semibold font-heading mb-4 flex items-center text-superone-charcoal">
        <Globe className="mr-2 h-5 w-5 text-orange-500" />
        Players by Country
      </h2>
      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <Table className="rounded-md overflow-hidden border border-gray-200">
            <TableHeader>
              <TableRow className="bg-[#0F87F2]">
                <TableHead
                  style={{ width: "80%" }}
                  className="text-white text-base whitespace-nowrap"
                >
                  Country
                </TableHead>
                <TableHead
                  style={{ width: "20%" }}
                  className="text-white text-base whitespace-nowrap text-right"
                >
                  Players
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedRows.map(([country, count]) => (
                <TableRow key={country}>
                  <TableCell
                    style={{ width: "80%" }}
                    className="whitespace-nowrap"
                  >
                    {country.charAt(0) + country.slice(1).toLowerCase()}
                  </TableCell>
                  <TableCell
                    style={{ width: "20%" }}
                    className="text-right font-semibold"
                  >
                    {count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between mt-4">
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${
                page === 1
                  ? "bg-gray-100 text-gray-400 cursor-default"
                  : "bg-[#0F87F2] text-white hover:bg-[#0C6DC2] cursor-pointer"
              }`}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${
                page === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-default"
                  : "bg-[#0F87F2] text-white hover:bg-[#0C6DC2] cursor-pointer"
              }`}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
